import React, { forwardRef, useEffect, useRef } from "react";

import { bemClassNames, createQA } from "@multiweb/reactUtils";
import { ForwardRefType, QAProps } from "@multiweb/types/react";
import { Role } from "@multiweb/uiShared/Role";
import { EncryptedSymbol } from "../../EncryptedSymbol";
import { Input, InputType } from "../../Input";
import { FormComponentProps } from "../../types";
import { generateFormComponentClasses } from "../../utils";
import { CheckBoxComponentSharedProps } from "../types";

export type CheckBoxFieldProps = FormComponentProps
    & CheckBoxComponentSharedProps
    & QAProps;

const classNames = bemClassNames("check-box-field-component");

/** Dvoustavové a třístavové zaškrtávací políčko bez popisku a chování */
export const CheckBoxField: ForwardRefType<HTMLDivElement, CheckBoxFieldProps> = forwardRef<HTMLDivElement, CheckBoxFieldProps>(({
    id,
    encrypted = false,
    hasThreeStates = false,
    invalid = false,
    disabled = false,
    required = false,
    backgroundColor,
    title,
    value,
    tabIndex,
    showFalseAsCrossed,
    qa,
}, ref) => {
    const checkboxRef = useRef<HTMLInputElement>(null);
    const classes = generateFormComponentClasses(classNames, { disabled, encrypted, invalid, required, backgroundColor }, [
        classNames({
            checked: !encrypted && value === true,
            crossed: (hasThreeStates || showFalseAsCrossed) && !encrypted && value === false,
            empty: !encrypted && value === undefined,
        }),
    ]);

    useEffect(() => {
        if (checkboxRef.current) {
            checkboxRef.current.checked = !!value;
            checkboxRef.current.indeterminate = value === undefined;
        }
    }, [value]);
    return (
        <div
            className={classes}
            role={Role.checkBox}
            aria-checked={value}
            {...{ id, tabIndex, title, ref }}
            {...createQA(qa)}
        >
            {encrypted && <EncryptedSymbol />}
            <Input
                type={InputType.hidden}
                ref={checkboxRef}
                disabled={disabled || encrypted}
                value={(value === undefined ? "" : value.toString())}
                {...{ required }}
                aria-invalid={invalid}
            />
        </div>
    );
});
